/* eslint-disable */

import { RACDateTimeLabel, Typography, withStyles } from '@rentacenter/racstrap';
import clsx from 'clsx';
import React from 'react';
import { getNameForAbbreviation, StateAbbreviation } from '../../domain/Common';
import { PrintLetterDetails } from '../../types/types';
import i18n from 'i18next';
import {
  formatDateString,
  formatPhoneNumberForPrintLetter,
} from '../../utils/utils';
import { StoreStateContext } from '../../context/Store/StoreProvider';

export interface HalfwayNoticeLetterProps {
  printLetterDetails: PrintLetterDetails;
  classes?: any;
  t: any;
}

const useStyles = (theme: any) => ({
  allLettersContainer: {
    width: '100%',
  },
  breakPage: {
    breakBefore: 'page',
  },
  halfwayNoticeContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    padding: theme.typography.pxToRem(30),
    paddingLeft: theme.typography.pxToRem(80),
    background: 'white',
  },
  section: {
    paddingBottom: theme.typography.pxToRem(30),
  },
  paragraph: {
    paddingBottom: theme.typography.pxToRem(15),
  },
  headerMetadata: {
    width: theme.typography.pxToRem(400),
  },
  underline: {
    textDecoration: 'underline',
    fontWeight: '500',
    fontFamily: 'OpenSans-semiBold',
  },
  alignLeft: {
    textAlign: 'left',
  },
  lineHeight7: {
    lineHeight: theme.typography.pxToRem(7),
  },
  margin20: {
    marginBottom: theme.typography.pxToRem(20),
  },
  bold: {
    fontFamily: 'OpenSans-bold',
  },
});

const dateFormat = 'MM-dd-yyyy';

class ComponentToPrintMx extends React.Component<HalfwayNoticeLetterProps> {
  static contextType = StoreStateContext;
  render() {
    const {
      classes,
      printLetterDetails: { customer, store, dateOfNotice },
    } = this.props;

    const { DateTimeLocalization } = this.context;

    return (
      <div className={classes.allLettersContainer}>
        {customer.map((cust, index) => {
          const {
            firstName,
            lastName,
            rentalAgreementExpiration,
            deadlineToReturnProperty,
          } = cust;

          const { address, phoneNumber } = store;
          const storeAddressFormatted = [
            address.addressLine1,
            address.addressLine2,
          ]
            .filter(Boolean)
            .join(', ');
          const storeCityStateZip = [
            address.city,
            getNameForAbbreviation(address.state as StateAbbreviation),
            address.zipCode,
          ]
            .filter(Boolean)
            .join(', ');
          const formattedPhone =
            formatPhoneNumberForPrintLetter(phoneNumber) || phoneNumber;

          const customerFullName = `${firstName} ${lastName}`;

          return (
            <span key={index}>
              <div className={classes.halfwayNoticeContainer}>
                {/* Store Header */}
                <div className={clsx(classes.section, classes.alignLeft)}>
                  <Typography align="left" variant="h6">
                    {/* Bold header */}
                    <span className={classes.bold}>
                      {
                        i18n
                          .t('storeAddress')
                          .split('[[[DOMICILIO TIENDA]]]')[0]
                      }
                    </span>
                    <br />
                    {/* Address content with line breaks */}
                    {`${storeAddressFormatted}, ${storeCityStateZip}`
                      .split('\n')
                      .map((line, index) => (
                        <React.Fragment key={index}>
                          {line}
                          <br />
                        </React.Fragment>
                      ))}
                  </Typography>
                </div>

                {/* Title */}
                <div className={clsx(classes.section, classes.alignLeft)}>
                  <Typography
                    variant="h5"
                    align="center"
                    className={classes.paragraph}
                  >
                    {i18n.t('title')}
                  </Typography>
                </div>

                {/* Dates */}
                <div className={clsx(classes.section, classes.alignLeft)}>
                  <Typography variant="h6" className={clsx(classes.paragraph)}>
                    <span className={classes.bold}>
                      {i18n.t('dateExpired')}
                    </span>
                    {
                      DateTimeLocalization ?
                      <RACDateTimeLabel 
                      value={rentalAgreementExpiration.split('T')[0]} 
                      country={this.context.store?.countryAbb} 
                      displayType={'ShortDate'}  
                      separator='-' className={classes.underline}/>
                      : 
                    <span className={classes.underline}>
                      {' '}
                      {formatDateString(
                        rentalAgreementExpiration.split('T')[0],
                        dateFormat
                      )}
                    </span>
                    }
                  </Typography>
                  <Typography variant="h6" className={clsx(classes.paragraph)}>
                    <span className={classes.bold}>{i18n.t('dateNotice')}</span>
                    {
                      DateTimeLocalization ?
                      <RACDateTimeLabel 
                      value={dateOfNotice.split('T')[0]} 
                      country={this.context.store?.countryAbb} 
                      displayType={'ShortDate'}  
                      separator='-' 
                      className={classes.underline}/>
                      : 
                    <span className={classes.underline}>
                      {' '}
                      {formatDateString(dateOfNotice.split('T')[0], dateFormat)}
                    </span>
                    }
                  </Typography>
                  <Typography variant="h6" className={clsx(classes.paragraph)}>
                    <span className={classes.bold}>
                      {i18n.t('deadlineToReturn')}
                    </span>
                    {
                      DateTimeLocalization ?
                      <RACDateTimeLabel 
                      value={deadlineToReturnProperty.split('T')[0]} 
                      country={this.context.store?.countryAbb} 
                      displayType={'ShortDate'}  
                      separator='-' 
                      className={classes.underline}/>
                      : 
                    <span className={classes.underline}>
                      {' '}
                      {formatDateString(
                        deadlineToReturnProperty.split('T')[0],
                        dateFormat
                      )}
                    </span>
                    }
                  </Typography>
                </div>

                {/* Greeting */}
                <div className={classes.paragraph}>
                  <Typography variant="h6" align="left">
                    <span className={classes.bold}>
                      {i18n
                        .t('greeting')
                        .replace('[[[NOMBRE DE CLIENTE]]]', customerFullName)}
                    </span>
                  </Typography>
                </div>

                {/* Body paragraphs */}
                <div className={classes.paragraph}>
                  <Typography align="left" variant="h6">
                    {i18n.t('line1')}
                  </Typography>
                </div>
                <div className={classes.paragraph}>
                  <Typography align="left" variant="h6">
                    {i18n.t('line2')}
                  </Typography>
                </div>
                <div className={classes.paragraph}>
                  <Typography align="left" variant="h6">
                    {i18n.t('line3')}
                  </Typography>
                </div>

                {/* Payment Options */}
                <div className={classes.paragraph}>
                  <Typography align="left" variant="h6">
                    {i18n.t('paymentOptionsHeader')}
                  </Typography>
                  <Typography align="left" variant="h6">
                    • {i18n.t('paymentOption1')}
                  </Typography>
                  <Typography align="left" variant="h6">
                    • {i18n.t('paymentOption2')}
                  </Typography>
                  <Typography align="left" variant="h6">
                    • {i18n.t('paymentOption3')}
                  </Typography>
                  <Typography align="left" variant="h6">
                    • {i18n.t('paymentOption4')}
                  </Typography>
                  <Typography align="left" variant="h6">
                    • {i18n.t('paymentOption5')}
                  </Typography>
                </div>

                {/* Warning */}
                <div className={classes.paragraph}>
                  <Typography align="left" variant="h6">
                    {i18n.t('warning')}
                  </Typography>
                </div>

                {/* Contact */}
                <div className={classes.paragraph}>
                  <Typography align="left" variant="h6">
                    {i18n
                      .t('contact')
                      .replace('[[[STOREPHONE]]]', formattedPhone)}
                  </Typography>
                </div>

                {/* Closing */}
                <div className={classes.paragraph}>
                  <Typography align="left" variant="h6">
                    {i18n.t('sincerely')}
                  </Typography>
                  <Typography
                    align="left"
                    variant="h6"
                    className={classes.underline}
                  >
                    {/* {storeManagerName} */}
                  </Typography>
                  <Typography align="left" variant="h6">
                    {i18n.t('storeMgr')}
                  </Typography>
                </div>
              </div>
              <div className={classes.breakPage}></div>
            </span>
          );
        })}
      </div>
    );
  }
}
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore

export default withStyles(useStyles)(ComponentToPrintMx);
